import { Link, useRouteContext } from "@tanstack/react-router";
import {
	differenceInDays,
	differenceInHours,
	differenceInMonths,
	differenceInYears,
} from "date-fns";
import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import {
	ConnectionHandler,
	graphql,
	useFragment,
	useMutation,
} from "react-relay";
import { Icon } from "@/components/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { MoveSpaceDialog } from "@/dialogs/post-move-dialog";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { cn } from "@/utils/cn";
import type { postHeader_deletePostMutation } from "./__generated__/postHeader_deletePostMutation.graphql";
import type { postHeader_pinPostMutation } from "./__generated__/postHeader_pinPostMutation.graphql";
import type { postHeader_unpinPostMutation } from "./__generated__/postHeader_unpinPostMutation.graphql";
import type { postHeader_updatePostMutation } from "./__generated__/postHeader_updatePostMutation.graphql";
import type { postHeaderFragment$key } from "./__generated__/postHeaderFragment.graphql";
import { MemberBadge } from "./member-badge";

export function PostHeader({
	post,
	showUniversalFeedPin,
	showSpaceFeedPin,
	onUpdate,
}: {
	post: postHeaderFragment$key;
	showUniversalFeedPin?: boolean;
	showSpaceFeedPin?: boolean;
	onUpdate?: () => void;
}) {
	const { track } = useAnalytics();
	const [hoveringName, setHoveringName] = useState(false);
	const [_reportDialogOpen, setReportDialogOpen] = useState(false);
	const [moveDialogOpen, setMoveDialogOpen] = useState(false);
	const { club, session } = useRouteContext({ from: "__root__" });

	const data = useFragment<postHeaderFragment$key>(
		graphql`
      fragment postHeaderFragment on Post @argumentDefinitions(club: { type: "String!" }) {
        id
        slug
        author {
          id
          displayName
          username
          avatarFallback
          badges(club: $club) {
              id
              ...memberBadgeFragment
          }
          photo {
	          signedUrl
          }
        }
        space {
          id
          name
          icon
          slug
        }
        club {
          id
          slug
        }
        createdAt
        pinned
        pinnedUniversal

        ...postMoveDialogQuery
      }
  `,
		post,
	);

	const [updatePost, _isPendingUpdate] =
		useMutation<postHeader_updatePostMutation>(graphql`
	    mutation postHeader_updatePostMutation($slug: String!, $payload: UpdatePostInput!, $club: String!) {
				updatePost(input: $payload, postSlug: $slug, club: $club) {
					id
					...postPreviewFragment @arguments(club: $club)
				}
			}
	`);
	const [pinPost, _isPendingPin] =
		useMutation<postHeader_pinPostMutation>(graphql`
	    mutation postHeader_pinPostMutation($id: ID!) {
				pinPost(id: $id) {
				  id
				}
			}
	`);
	const [unpinPost, _isPendingUnpin] =
		useMutation<postHeader_unpinPostMutation>(graphql`
	    mutation postHeader_unpinPostMutation($id: ID!) {
				unpinPost(id: $id) {
				  id
				}
			}
	`);
	const [deletePost, _isPendingDelete] =
		useMutation<postHeader_deletePostMutation>(graphql`
	    mutation postHeader_deletePostMutation($id: ID!) {
				deletePost(id: $id) {
				  id @deleteRecord
				}
			}
	`);

	const createdDistance = useMemo(() => {
		const currentDate = new Date();
		const hours = differenceInHours(currentDate, data.createdAt);

		if (hours < 1) {
			return "alguns minutos";
		}

		if (hours < 24) {
			return hours === 1 ? "1 hora" : `${hours} horas`;
		}

		const days = differenceInDays(currentDate, data.createdAt);
		if (days < 30) {
			return days === 1 ? "1 dia" : `${days} dias`;
		}

		const months = differenceInMonths(currentDate, data.createdAt);

		if (months < 1) {
			return days === 1 ? "1 dia" : `${days} dias`;
		}

		if (months < 12) {
			return months === 1 ? "1 mês" : `${months} meses`;
		}

		const years = differenceInYears(currentDate, data.createdAt);

		return years === 1 ? "mais de 1 ano" : `mais de ${years} anos`;
	}, [data.createdAt]);

	const { author } = data;

	const isAdmin = club?.permissions.some(
		(p) => p.slug === "CLUB_ADMIN" || p.slug === "MODERATOR",
	);
	const isAuthor = session?.user && author && author.id === session?.user?.id;

	const pinToUniversalFeed = () => {
		if (
			confirm("Tem certeza que deseja fixar este post na timeline principal?")
		) {
			track(AnalyticsEvents.POST_PINNED, {
				post_id: data.id,
				pin_type: "universal",
			});
			updatePost({
				variables: {
					slug: data.slug,
					payload: { pinnedUniversal: true },
					club: data.club.slug,
				},
				updater: (store) => {
					const clubRecord = store.get(data.club.id);

					if (clubRecord) {
						const bestConnection = ConnectionHandler.getConnection(
							clubRecord,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "BEST" },
						);

						const newestConnection = ConnectionHandler.getConnection(
							clubRecord,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "NEWEST" },
						);

						const oldestConnection = ConnectionHandler.getConnection(
							clubRecord,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "OLDEST" },
						);

						bestConnection?.invalidateRecord();
						newestConnection?.invalidateRecord();
						oldestConnection?.invalidateRecord();

						onUpdate?.();
					}
				},
			});
		}
	};

	const unpinFromUniversalFeed = () => {
		if (
			confirm(
				"Tem certeza que deseja desafixar este post da timeline principal?",
			)
		) {
			track(AnalyticsEvents.POST_UNPINNED, {
				post_id: data.id,
				pin_type: "universal",
			});
			updatePost({
				variables: {
					slug: data.slug,
					payload: { pinnedUniversal: false },
					club: data.club.slug,
				},
				updater: (store) => {
					const clubRecord = store.get(data.club.id);

					if (clubRecord) {
						const bestConnection = ConnectionHandler.getConnection(
							clubRecord,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "BEST" },
						);

						const newestConnection = ConnectionHandler.getConnection(
							clubRecord,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "NEWEST" },
						);

						const oldestConnection = ConnectionHandler.getConnection(
							clubRecord,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "OLDEST" },
						);

						bestConnection?.invalidateRecord();
						newestConnection?.invalidateRecord();
						oldestConnection?.invalidateRecord();

						onUpdate?.();
					}
				},
			});
		}
	};

	const pinToSpaceFeed = () => {
		if (confirm("Tem certeza que deseja fixar este post?")) {
			track(AnalyticsEvents.POST_PINNED, {
				post_id: data.id,
				pin_type: "space",
			});
			pinPost({
				variables: { id: data.id },
				updater: (store) => {
					if (data.space) {
						const item = store.get(data.space?.id);

						if (item) {
							const connection = ConnectionHandler.getConnection(
								item,
								"spaceFeedFragment__feed",
							);

							if (connection) {
								connection.invalidateRecord();
							}
						}
					}
				},
			});
		}
	};

	const unpinFromSpaceFeed = () => {
		if (confirm("Tem certeza que deseja desafixar este post?")) {
			track(AnalyticsEvents.POST_UNPINNED, {
				post_id: data.id,
				pin_type: "space",
			});
			unpinPost({
				variables: { id: data.id },
				updater: (store) => {
					if (data.space) {
						const item = store.get(data.space?.id);

						if (item) {
							const bestConnection = ConnectionHandler.getConnection(
								item,
								"spaceFeedFragment__feed",
								{ ignoreFixed: false, orderBy: "BEST" },
							);

							const newestConnection = ConnectionHandler.getConnection(
								item,
								"spaceFeedFragment__feed",
								{ ignoreFixed: false, orderBy: "NEWEST" },
							);

							const oldestConnection = ConnectionHandler.getConnection(
								item,
								"spaceFeedFragment__feed",
								{ ignoreFixed: false, orderBy: "OLDEST" },
							);

							bestConnection?.invalidateRecord();
							newestConnection?.invalidateRecord();
							oldestConnection?.invalidateRecord();

							onUpdate?.();
						}
					}
				},
			});
		}
	};

	return (
		<>
			<MoveSpaceDialog
				open={moveDialogOpen}
				onClose={() => setMoveDialogOpen(false)}
				post={data}
			/>

			<div className="flex flex-row items-center justify-between">
				<div className="flex flex-row items-center justify-start gap-2">
					<Avatar>
						{data.author?.photo?.signedUrl ? (
							<AvatarImage src={data.author?.photo?.signedUrl} />
						) : null}
						<AvatarFallback>{data.author?.avatarFallback}</AvatarFallback>
					</Avatar>

					<div className="flex flex-col">
						<AnimatePresence>
							<div className="flex items-center justify-start gap-2">
								<Link
									to="/u/$username"
									params={{ username: data.author?.username! }}
									className={cn(
										"text-sm/6 font-semibold no-underline text-foreground flex flex-row items-center justify-start",
									)}
									onMouseEnter={() => setHoveringName(true)}
									onMouseLeave={() => setHoveringName(false)}
								>
									<motion.span
										key="author-title"
										className={cn(
											"flex flex-row items-center justify-start gap-2 rounded",
											hoveringName &&
												"bg-muted text-muted-foreground px-2 transition-all",
										)}
										style={{
											paddingLeft: hoveringName ? 8 : 0,
											paddingRight: hoveringName ? 8 : 0,
										}}
									>
										{data.author?.displayName}

										{hoveringName && (
											<motion.div
												key="author-icon"
												initial={{ opacity: 0, translateX: -5 }}
												animate={{ opacity: 1, translateX: 0 }}
												exit={{ opacity: 0, translateX: -5 }}
											>
												<Icon
													name="MoveUpRight"
													className="size-3 text-muted-foreground"
												/>
											</motion.div>
										)}
									</motion.span>
								</Link>

								{data.author?.badges?.length ? (
									<div className="flex flex-row items-center justify-start gap-1 max-sm:hidden">
										<MemberBadge badge={data.author.badges[0]} />

										{data.author?.badges.length > 1 && (
											<Tooltip>
												<TooltipTrigger asChild>
													<button
														type="button"
														className="text-[10px] font-medium px-2 py-0.5 rounded-sm bg-muted text-foreground"
													>
														+{data.author?.badges.length - 1}
													</button>
												</TooltipTrigger>

												<TooltipContent className="flex flex-col gap-2">
													{data.author.badges.slice(1).map((badge) => (
														<div key={badge.id}>
															<MemberBadge badge={badge} />
														</div>
													))}
												</TooltipContent>
											</Tooltip>
										)}
									</div>
								) : null}
							</div>
						</AnimatePresence>
						<span className="text-xs text-muted-foreground">
							Publicado há {createdDistance}
						</span>
					</div>
				</div>

				<div className="flex flex-col">
					<div className="flex items-center justify-end gap-2">
						{!(data.pinnedUniversal && showUniversalFeedPin) &&
							!(data.pinned && showSpaceFeedPin) && (
								<Link
									to={"/comunidade/s/$space"}
									params={{ space: data.space?.slug! }}
									className="leading-none max-sm:hidden"
								>
									<Badge variant="default" className="no-underline">
										{`${data.space?.icon || ""} ${data.space?.name}`.trim()}
									</Badge>
								</Link>
							)}

						{data.pinnedUniversal && showUniversalFeedPin && (
							<Badge variant="secondary">
								<Icon name="Pin" className="size-3 rotate-45" />
								Fixado
							</Badge>
						)}

						{data.pinned && showSpaceFeedPin && (
							<Badge variant="secondary">
								<Icon name="Pin" className="size-3 rotate-45" />
								Fixado
							</Badge>
						)}

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button size="icon" variant="ghost">
									<Icon data-slot="icon" name="EllipsisVertical" />
								</Button>
							</DropdownMenuTrigger>

							<DropdownMenuContent>
								{isAdmin || isAuthor ? (
									<>
										<Link to="/posts/$slug/editar" params={{ slug: data.slug }}>
											<DropdownMenuItem>
												<Icon data-slot="icon" name={"Pencil"} />
												Editar Post
											</DropdownMenuItem>
										</Link>

										<DropdownMenuItem
											onClick={
												data.pinned ? unpinFromSpaceFeed : pinToSpaceFeed
											}
										>
											<Icon
												data-slot="icon"
												name={data.pinned ? "PinOff" : "Pin"}
											/>
											{data.pinned ? "Desafixar" : "Fixar"} no feed{" "}
											{data.space?.name}
										</DropdownMenuItem>

										<DropdownMenuItem
											onClick={
												data.pinnedUniversal
													? unpinFromUniversalFeed
													: pinToUniversalFeed
											}
										>
											<Icon
												data-slot="icon"
												name={data.pinnedUniversal ? "PinOff" : "Pin"}
											/>
											{data.pinnedUniversal ? "Desafixar" : "Fixar"} na Página
											Inicial
										</DropdownMenuItem>

										<DropdownMenuItem onClick={() => setMoveDialogOpen(true)}>
											<Icon data-slot="icon" name="Repeat" />
											Mover de Feed
										</DropdownMenuItem>
									</>
								) : (
									<DropdownMenuItem onClick={() => setReportDialogOpen(true)}>
										<Icon data-slot="icon" name="Flag" />
										Reportar
									</DropdownMenuItem>
								)}

								{isAuthor || isAdmin ? (
									<DropdownMenuItem
										onClick={() => {
											if (
												confirm("Tem certeza que deseja deletar este post?")
											) {
												track(AnalyticsEvents.POST_DELETED, {
													post_id: data.id,
												});
												deletePost({ variables: { id: data.id } });
											}
										}}
									>
										<Icon data-slot="icon" name="Trash2" />
										Deletar Post
									</DropdownMenuItem>
								) : null}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
					<span className="text-xs text-muted-foreground">&nbsp;</span>
				</div>
			</div>
		</>
	);
}
